import { StormBuildUpRowDef } from './stormBuildUpTypes';

// exTopsoilMm defaults to Global Settings' "Ex Topsoil Depth Utilised" fallback
// (0.3m × 1000, same `?? 0.3` convention as FoulSewersTakeoff.tsx/RoadsTakeoff.tsx)
// so callers with no live state (Excel import/export, Spoil Reconciliation,
// which key off `.id` not `.description`) still get a sane row.
// Per-section configurable field, same registry shape as posRowDefs.ts's
// POS_CONFIGURABLE_FIELDS — only one field for Ponds today (topsoil spread
// thickness), kept as a one-entry array/lookup so the Configure modal and
// row-id parsing stay consistent with that pattern.
export interface PondConfigurableField {
  suffix: string; // row id suffix, e.g. `pond_2_${suffix}`
  label: string;
  describe: (mm: number) => string;
}

export const POND_CONFIGURABLE_FIELDS: PondConfigurableField[] = [
  { suffix: 'topsoilSiteWon', label: 'Top soil (site won), xxxmm thick', describe: (mm) => `Spread and level ${mm}mm thick top soil - site won` },
  { suffix: 'topsoilImported', label: 'Top soil (imported), xxxmm thick', describe: (mm) => `Spread and level ${mm}mm thick top soil - imported` },
];

// No default value: matches posRowDefs.ts's convention — an unconfigured row
// keeps showing this static "xxx" placeholder text, only switching to the
// real mm value once that section's Configure modal sets one > 0.
const POND_TOPSOIL_SITEWON_PLACEHOLDER = 'Spread and level xxxmm thick top soil - site won';
const POND_TOPSOIL_IMPORTED_PLACEHOLDER = 'Spread and level xxxmm thick top soil - imported';

export const makePondRows = (n: number, exTopsoilMm: number = 300, topsoilSiteWonMm?: number, topsoilImportedMm?: number, sectionTitle?: string): StormBuildUpRowDef[] => [
  { id: `pond_${n}_hdr`, description: `POND ${n}${sectionTitle?.trim() ? ` ${sectionTitle.trim().toUpperCase()}` : ''}`, unit: '', defaultQuant: null, titleSuffix: sectionTitle?.trim() ? sectionTitle.trim().toUpperCase() : undefined, isHeader: true },
  { id: `pond_${n}_strip`, description: 'Site strip to remove vegetation', unit: 'm2', defaultQuant: null },
  { id: `pond_${n}_excTop`, description: `Excavate to reduce levels to take up ${exTopsoilMm}mm top soil, deposit onsite`, unit: 'm3', defaultQuant: null },
  { id: `pond_${n}_excLvl`, description: 'Excavate to reduce levels', unit: 'm3', defaultQuant: null },
  { id: `pond_${n}_fill`, description: 'Filling with site won subsoil', unit: 'm3', defaultQuant: null },
  { id: `pond_${n}_cart`, description: 'Load, cart and bund excavated material', unit: 'm3', defaultQuant: null },
  { id: `pond_${n}_level`, description: 'Level and compact formation, batter embankments', unit: 'm2', defaultQuant: null },
  { id: `pond_${n}_lining`, description: 'Pond lining or puddle clay', unit: 'm2', defaultQuant: null },
  { id: `pond_${n}_anchor`, description: 'Anchor trench', unit: 'm', defaultQuant: null },
  { id: `pond_${n}_topsoilSiteWon`, description: topsoilSiteWonMm ? POND_CONFIGURABLE_FIELDS[0].describe(topsoilSiteWonMm) : POND_TOPSOIL_SITEWON_PLACEHOLDER, unit: 'm2', defaultQuant: null },
  { id: `pond_${n}_topsoilImported`, description: topsoilImportedMm ? POND_CONFIGURABLE_FIELDS[1].describe(topsoilImportedMm) : POND_TOPSOIL_IMPORTED_PLACEHOLDER, unit: 'm2', defaultQuant: null },
];

export const DEFAULT_POND_SECTIONS = 1;
export const MAX_POND_SECTIONS = 15;

// rowId is the full `pond_<n>_<suffix>` id — parses out the suffix and looks
// up its registry entry, if any (mirrors posRowDefs.ts's getPOSConfigurableField).
export const getPondConfigurableField = (rowId: string): PondConfigurableField | undefined => {
  const match = rowId.match(/^pond_\d+_(.+)$/);
  if (!match) return undefined;
  return POND_CONFIGURABLE_FIELDS.find((f) => f.suffix === match[1]);
};

export const getPondsRows = (
  sectionCount: number,
  exTopsoilMm?: number,
  pondTopsoilThicknessMm: Record<string, number> = {},
  sectionTitles: Record<string, string> = {}
): StormBuildUpRowDef[] =>
  Array.from({ length: sectionCount }, (_, i) => {
    const n = i + 1;
    const topsoilSiteWonMm = pondTopsoilThicknessMm[`pond_${n}_topsoilSiteWon`];
    const topsoilImportedMm = pondTopsoilThicknessMm[`pond_${n}_topsoilImported`];
    return makePondRows(n, exTopsoilMm, topsoilSiteWonMm, topsoilImportedMm, sectionTitles[`pond_${n}_hdr`]);
  }).flat();

// Full fixed set — used by Spoil Reconciliation / Excel import-export, which
// need every POND 1-N row id available regardless of how many sections the
// build-up UI currently shows.
export const PONDS_ROWS: StormBuildUpRowDef[] = getPondsRows(MAX_POND_SECTIONS);

// Shifts a row-id-keyed map's `pond_<n>_*` keys down by one wherever n >
// deletedN, and drops the deleted section's own keys — mirrors posRowDefs.ts's
// shiftPosSectionKeys. Keys with other prefixes (swale_/hw_/ps_, which share
// the same pondsSwales.table) pass through untouched.
export const shiftPondSectionKeys = <T,>(map: Record<string, T>, deletedN: number): Record<string, T> => {
  const next: Record<string, T> = {};
  Object.entries(map).forEach(([id, value]) => {
    const match = id.match(/^pond_(\d+)_(.+)$/);
    if (!match) {
      next[id] = value;
      return;
    }
    const idx = Number(match[1]);
    if (idx === deletedN) return;
    const newIdx = idx > deletedN ? idx - 1 : idx;
    next[`pond_${newIdx}_${match[2]}`] = value;
  });
  return next;
};
